import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useParams, Navigate } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa';
import {
  CircularProgress,
  Alert,
  Box,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import RadarIcon from '@mui/icons-material/Radar';
import DescriptionIcon from '@mui/icons-material/Description';
import AddIcon from '@mui/icons-material/Add';
import SyncIcon from '@mui/icons-material/Sync';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import SchoolIcon from '@mui/icons-material/School';
import WorkIcon from '@mui/icons-material/Work';
import InboxIcon from '@mui/icons-material/Inbox';
import { employeesApi, competencyTemplatesApi } from '../../../services/api';
import { COLORS } from '../../../constants/radar';
import { SKILL_TYPES } from '../../../constants/skillTypes';
import { CV_TECHNOLOGY_SOURCES } from '../../../constants/cvTechnologySources';
import { useAlert } from '../../../contexts/AlertContext';
import ReviewerCompetency from '../../EmployeesRepository/EmployeeForm/components/AssignmentDetails/components/ReviewerCompetency';
import TechnologyHierarchyPath from '../TechnologyComparisonDialog/TechnologyHierarchyPath';
import TechnologySyncDialog, { OPERATION_TYPES } from '../TechnologyComparisonDialog/TechnologySyncDialog';
import CVCardItem from '../../CVRepository/components/CVCardItem';
import {
  Container,
  Header,
  HeaderLeft,
  BackButton,
  Title,
  VersionBadge,
  Content,
} from './styles';
import {
  TabContainer,
  TabButton,
} from '../../EmployeesRepository/EmployeeForm/styles';
import {
  SummaryContainer,
  SummaryCard,
  ComparisonSection,
  SectionHeader,
  SectionContent,
  TechCard,
  LevelBadge,
  StatusBadge,
  SourceBadge,
  DifferenceBadge,
  ActionButton,
  EmptyState,
} from '../TechnologyComparisonDialog/styles';

const TechnologyComparison = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { employeeId } = useParams();
  const { showAlert } = useAlert();
  const employeeUser = location.state?.user;
  const fromLandingPage = location.state?.fromLandingPage || false;
  // ADR-109 §3 — forwarded both ways so Technology Configuration can still return to the Individual.
  const individualReturn = location.state?.individualReturn || null;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [comparisonData, setComparisonData] = useState([]);
  const [skillLevels, setSkillLevels] = useState([]);
  const [selectedTabIndex, setSelectedTabIndex] = useState(0);

  // Sync dialog state
  const [syncDialogOpen, setSyncDialogOpen] = useState(false);
  const [syncOperationType, setSyncOperationType] = useState(null);
  const [syncTechnology, setSyncTechnology] = useState(null);
  const [syncLoading, setSyncLoading] = useState(false);
  const [syncError, setSyncError] = useState(null);

  useEffect(() => {
    const fetchCompetencyTemplates = async () => {
      try {
        const templates = await competencyTemplatesApi.getTemplatesByType(SKILL_TYPES.TECHNICAL);
        if (templates && templates.length > 0 && templates[0].levels) {
          const transformedLevels = templates[0].levels.map(level => ({
            level: level.level,
            displayName: level.name,
            color: level.color,
            defaultDescription: level.description,
            description: level.description,
            levelId: level.id
          }));
          setSkillLevels(transformedLevels);
        }
      } catch (error) {
        console.error('Failed to fetch competency templates:', error);
      }
    };

    fetchCompetencyTemplates();
  }, []);

  const effectiveEmployeeId = employeeId || employeeUser?.id;

  useEffect(() => {
    if (effectiveEmployeeId) {
      fetchComparisonData();
    }
  }, [effectiveEmployeeId]);

  const fetchComparisonData = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await employeesApi.getTechnologyComparison(effectiveEmployeeId);
      // Handle both array format (new) and single object format (legacy)
      const normalizedData = Array.isArray(data) ? data : [{ cv: null, comparison: data.comparison, summary: data.summary }];
      setComparisonData(normalizedData);
    } catch (err) {
      setError(err.message || 'Failed to fetch comparison data');
    } finally {
      setLoading(false);
    }
  };

  const currentCVData = comparisonData[selectedTabIndex] || null;
  const currentCvId = currentCVData?.cv?.id;

  // Sync dialog handlers
  const openSyncDialog = (operationType, technology) => {
    setSyncOperationType(operationType);
    setSyncTechnology(technology);
    setSyncError(null);
    setSyncDialogOpen(true);
  };

  const closeSyncDialog = () => {
    setSyncDialogOpen(false);
    setSyncOperationType(null);
    setSyncTechnology(null);
    setSyncError(null);
  };

  const handleSyncConfirm = async (technology, competencyLevelId) => {
    setSyncLoading(true);
    setSyncError(null);

    try {
      switch (syncOperationType) {
        case OPERATION_TYPES.ADD_TO_RADAR:
          await employeesApi.addTechnologyToRadar(effectiveEmployeeId, {
            leafSegmentId: technology.leafSegmentId,
            competencyLevelId,
          });
          showAlert('Technology added to Tech Radar successfully', 'success');
          break;

        case OPERATION_TYPES.ADD_TO_CV:
          await employeesApi.addTechnologyToCV(effectiveEmployeeId, {
            cvId: currentCvId,
            leafSegmentId: technology.leafSegmentId,
            competencyLevelId,
          });
          showAlert('Technology added to CV successfully', 'success');
          break;

        case OPERATION_TYPES.SYNC_TO_CV:
          await employeesApi.syncTechnologyToCV(effectiveEmployeeId, {
            cvId: currentCvId,
            leafSegmentId: technology.leafSegmentId,
            competencyLevelId,
          });
          showAlert('Technology synced to CV successfully', 'success');
          break;

        case OPERATION_TYPES.SYNC_TO_RADAR:
          await employeesApi.syncTechnologyToRadar(effectiveEmployeeId, {
            leafSegmentId: technology.leafSegmentId,
            competencyLevelId,
          });
          showAlert('Technology synced to Tech Radar successfully', 'success');
          break;

        default:
          throw new Error('Unknown operation type');
      }

      // Refresh comparison data after successful sync
      await fetchComparisonData();
      closeSyncDialog();
    } catch (err) {
      setSyncError(err.message || 'Failed to perform sync operation');
    } finally {
      setSyncLoading(false);
    }
  };

  const renderLevel = (level) => {
    if (!level) {
      return <LevelBadge $bgColor="#e0e0e0" $textColor="#666">Not Set</LevelBadge>;
    }
    return (
      <Box sx={{
        '& > div': {
          width: 'auto !important',
        },
        '& > div > div': {
          padding: '4px 8px !important',
          width: 'auto !important',
          minWidth: '100px',
        },
        '& > div > div > div': {
          gap: '4px !important',
        },
        '& > div > div > div > div': {
          width: '8px !important',
          height: '8px !important',
        },
        '& > div > div > div > p': {
          fontSize: '11px !important',
        },
      }}>
        <ReviewerCompetency
          reviewer={{ employee: { fullName: '' } }}
          competencyLevelId={level.id}
          opacity={1}
          hideReviewer={true}
          skillLevels={skillLevels}
        />
      </Box>
    );
  };

  const renderSourceBadge = (source) => {
    if (!source) return null;
    return (
      <SourceBadge $source={source}>
        {source === CV_TECHNOLOGY_SOURCES.GENERAL_SKILL ? <SchoolIcon /> : <WorkIcon />}
        {source === CV_TECHNOLOGY_SOURCES.GENERAL_SKILL ? 'General Skill' : 'Employment'}
      </SourceBadge>
    );
  };

  const renderMatchedSection = (comparison) => {
    const items = comparison?.matched || [];
    if (items.length === 0) return null;

    return (
      <ComparisonSection>
        <SectionHeader $bgColor="#dcfce7" $textColor="#166534" $countBgColor="#166534">
          <CheckCircleIcon />
          Matched Technologies
          <span className="count">{items.length}</span>
        </SectionHeader>
        <SectionContent>
          {items.map((item) => (
            <TechCard key={item.leafSegmentId}>
              <div className="left">
                <span className="tech-name">{item.name}</span>
                <TechnologyHierarchyPath hierarchy={item.hierarchy} hideFirstLevel hideLastLevel />
                <div className="tech-meta">
                  {renderLevel(item.level)}
                  {renderSourceBadge(item.cvSource)}
                </div>
              </div>
              <div className="right">
                <StatusBadge $status="matched">
                  <CheckCircleIcon /> Match
                </StatusBadge>
              </div>
            </TechCard>
          ))}
        </SectionContent>
      </ComparisonSection>
    );
  };

  const renderMismatchSection = (comparison) => {
    const items = comparison?.levelMismatch || [];
    if (items.length === 0) return null;

    return (
      <ComparisonSection>
        <SectionHeader $bgColor="#fef3c7" $textColor="#92400e" $countBgColor="#d97706">
          <WarningIcon />
          Level Mismatches
          <span className="count">{items.length}</span>
        </SectionHeader>
        <SectionContent sx={{ display: 'block' }}>
          {items.map((item) => (
            <Box key={item.leafSegmentId} sx={{ mb: 2, '&:last-child': { mb: 0 } }}>
              <Box sx={{
                fontWeight: 500,
                color: COLORS.PRIMARY,
                fontSize: '14px',
                mb: 1,
                pb: 1,
                borderBottom: '1px solid #e8e8e8'
              }}>
                {item.name}
                <TechnologyHierarchyPath hierarchy={item.hierarchy} hideFirstLevel hideLastLevel />
              </Box>
              <Box sx={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 2,
                backgroundColor: '#fafafa',
                borderRadius: '8px',
                p: 1.5
              }}>
                {/* Left - Radar */}
                <Box sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                  borderRight: '1px solid #e8e8e8',
                  pr: 2
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: COLORS.PRIMARY, fontSize: '12px', fontWeight: 500 }}>
                    <RadarIcon sx={{ fontSize: 14 }} />
                    Tech Radar
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {renderLevel(item.radarLevel)}
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                    <DifferenceBadge $positive={item.levelDifference > 0}>
                      {item.levelDifference > 0 ? <ArrowUpwardIcon /> : <ArrowDownwardIcon />}
                      {Math.abs(item.levelDifference)}
                    </DifferenceBadge>
                    <ActionButton
                      onClick={() => openSyncDialog(OPERATION_TYPES.SYNC_TO_CV, item)}
                      disabled={item.cvSource === CV_TECHNOLOGY_SOURCES.EMPLOYMENT}
                      title={item.cvSource === CV_TECHNOLOGY_SOURCES.EMPLOYMENT ? 'Cannot sync to CV - technology is from Employment' : ''}
                    >
                      <SyncIcon /> Sync to CV
                    </ActionButton>
                  </Box>
                </Box>
                {/* Right - CV */}
                <Box sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                  pl: 1
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#6b7280', fontSize: '12px', fontWeight: 500 }}>
                    <DescriptionIcon sx={{ fontSize: 14 }} />
                    CV
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {renderLevel(item.cvLevel)}
                    {renderSourceBadge(item.cvSource)}
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                    <DifferenceBadge $positive={item.levelDifference < 0}>
                      {item.levelDifference < 0 ? <ArrowUpwardIcon /> : <ArrowDownwardIcon />}
                      {Math.abs(item.levelDifference)}
                    </DifferenceBadge>
                    <ActionButton
                      onClick={() => openSyncDialog(OPERATION_TYPES.SYNC_TO_RADAR, item)}
                      disabled={item.cvSource === CV_TECHNOLOGY_SOURCES.EMPLOYMENT}
                      title={item.cvSource === CV_TECHNOLOGY_SOURCES.EMPLOYMENT ? 'Cannot sync to Radar - technology is from Employment' : ''}
                    >
                      <SyncIcon /> Sync to Radar
                    </ActionButton>
                  </Box>
                </Box>
              </Box>
            </Box>
          ))}
        </SectionContent>
      </ComparisonSection>
    );
  };

  const renderOnlyInRadarSection = (comparison) => {
    const items = comparison?.onlyInRadar || [];
    if (items.length === 0) return null;

    return (
      <ComparisonSection>
        <SectionHeader $bgColor="#fee2e2" $textColor="#991b1b" $countBgColor="#dc2626">
          <RadarIcon />
          Only in Tech Radar
          <span className="count">{items.length}</span>
        </SectionHeader>
        <SectionContent>
          {items.map((item) => (
            <TechCard key={item.leafSegmentId}>
              <div className="left">
                <span className="tech-name">{item.name}</span>
                <TechnologyHierarchyPath hierarchy={item.hierarchy} hideFirstLevel hideLastLevel />
                <div className="tech-meta">
                  {renderLevel(item.radarLevel)}
                </div>
              </div>
              <div className="right">
                <ActionButton onClick={() => openSyncDialog(OPERATION_TYPES.ADD_TO_CV, item)}>
                  <AddIcon /> Add to CV
                </ActionButton>
              </div>
            </TechCard>
          ))}
        </SectionContent>
      </ComparisonSection>
    );
  };

  const renderOnlyInCVSection = (comparison) => {
    const items = comparison?.onlyInCV || [];
    if (items.length === 0) return null;

    return (
      <ComparisonSection>
        <SectionHeader $bgColor="#dbeafe" $textColor="#1e40af" $countBgColor="#2563eb">
          <DescriptionIcon />
          Only in CV
          <span className="count">{items.length}</span>
        </SectionHeader>
        <SectionContent>
          {items.map((item) => (
            <TechCard key={item.leafSegmentId}>
              <div className="left">
                <span className="tech-name">{item.name}</span>
                <TechnologyHierarchyPath hierarchy={item.hierarchy} hideFirstLevel hideLastLevel />
                <div className="tech-meta">
                  {renderLevel(item.cvLevel)}
                  {renderSourceBadge(item.source)}
                </div>
              </div>
              <div className="right">
                <ActionButton onClick={() => openSyncDialog(OPERATION_TYPES.ADD_TO_RADAR, item)}>
                  <AddIcon /> Add to Radar
                </ActionButton>
              </div>
            </TechCard>
          ))}
        </SectionContent>
      </ComparisonSection>
    );
  };

  const renderSummary = (summary) => {
    if (!summary) return null;

    return (
      <SummaryContainer>
        <SummaryCard $bgColor="#f0fdf4" $borderColor="#86efac" $iconBgColor="#16a34a" $textColor="#166534">
          <div className="icon"><CheckCircleIcon sx={{ fontSize: 16 }} /></div>
          <div className="content">
            <span className="count">{summary.matchedCount}</span>
            <span className="label">Matched</span>
          </div>
        </SummaryCard>
        <SummaryCard $bgColor="#fefce8" $borderColor="#fde047" $iconBgColor="#ca8a04" $textColor="#854d0e">
          <div className="icon"><WarningIcon sx={{ fontSize: 16 }} /></div>
          <div className="content">
            <span className="count">{summary.levelMismatchCount}</span>
            <span className="label">Level Mismatch</span>
          </div>
        </SummaryCard>
        <SummaryCard $bgColor="#fef2f2" $borderColor="#fca5a5" $iconBgColor="#dc2626" $textColor="#991b1b">
          <div className="icon"><RadarIcon sx={{ fontSize: 16 }} /></div>
          <div className="content">
            <span className="count">{summary.onlyInRadarCount}</span>
            <span className="label">Only in Radar</span>
          </div>
        </SummaryCard>
        <SummaryCard $bgColor="#eff6ff" $borderColor="#93c5fd" $iconBgColor="#2563eb" $textColor="#1e40af">
          <div className="icon"><DescriptionIcon sx={{ fontSize: 16 }} /></div>
          <div className="content">
            <span className="count">{summary.onlyInCVCount}</span>
            <span className="label">Only in CV</span>
          </div>
        </SummaryCard>
      </SummaryContainer>
    );
  };

  const renderEmptyState = (comparison) => {
    const hasNoData = comparison &&
      comparison.matched?.length === 0 &&
      comparison.levelMismatch?.length === 0 &&
      comparison.onlyInRadar?.length === 0 &&
      comparison.onlyInCV?.length === 0;

    if (!hasNoData) return null;

    return (
      <EmptyState>
        <InboxIcon />
        <div className="title">No Technologies to Compare</div>
        <div className="description">
          This employee has no technologies in either Tech Radar or CV.
        </div>
      </EmptyState>
    );
  };

  const renderCVComparison = (cvData) => {
    if (!cvData) return null;

    const { cv, comparison, summary } = cvData;

    return (
      <Box>
        {/* Summary and CV Card side by side */}
        <Box sx={{ display: 'flex', gap: 3, mb: 3, alignItems: 'flex-start' }}>
          {/* CV Card */}
          {cv && (
            <Box sx={{ width: '400px', flexShrink: 0 }}>
              <CVCardItem
                cv={cv}
                onClick={(cv) => navigate(`/cv-repository/${cv.id}/view`, {
                  state: {
                    returnTo: location.pathname,
                    returnState: { user: employeeUser, fromLandingPage, individualReturn }
                  }
                })}
                onView={(cv) => navigate(`/cv-repository/${cv.id}/view`, {
                  state: {
                    returnTo: location.pathname,
                    returnState: { user: employeeUser, fromLandingPage, individualReturn }
                  }
                })}
              />
            </Box>
          )}
          {/* Summary */}
          <Box sx={{ flex: 1 }}>
            {renderSummary(summary)}
          </Box>
        </Box>

        {/* Comparison Sections */}
        {renderEmptyState(comparison)}
        {renderMatchedSection(comparison)}
        {renderMismatchSection(comparison)}
        {renderOnlyInRadarSection(comparison)}
        {renderOnlyInCVSection(comparison)}
      </Box>
    );
  };

  if (!effectiveEmployeeId) {
    return <Navigate to="/" />;
  }

  return (
    <Container>
      <Header>
        <HeaderLeft>
          <BackButton onClick={() => navigate(`/employee-radar/${effectiveEmployeeId}`, { state: { user: employeeUser, fromLandingPage, individualReturn } })}>
            <FaArrowLeft /> Back to Technology Configuration
          </BackButton>
          <Title>Technology Sync{employeeUser?.name ? ` | ${employeeUser.name}` : ''}</Title>
        </HeaderLeft>
      </Header>

      {/* Tabs for multiple CVs - outside Content like in EmployeeForm */}
      {!loading && !error && comparisonData.length > 1 && (
        <TabContainer>
          {comparisonData.map((cvData, index) => (
            <TabButton
              key={cvData.cv?.id || index}
              data-active={selectedTabIndex === index}
              onClick={() => setSelectedTabIndex(index)}
            >
              {cvData.cv?.fullName || `CV ${index + 1}`}{' '}
              <VersionBadge style={{ marginLeft: '8px' }}>v.{cvData.cv?.version || 1}</VersionBadge>
            </TabButton>
          ))}
        </TabContainer>
      )}

      <Content>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!loading && !error && comparisonData.length > 0 && renderCVComparison(currentCVData)}

        {!loading && !error && comparisonData.length === 0 && (
          <EmptyState>
            <InboxIcon />
            <div className="title">No CVs Found</div>
            <div className="description">
              This employee has no CVs to compare.
            </div>
          </EmptyState>
        )}
      </Content>

      {/* Sync Confirmation Dialog */}
      <TechnologySyncDialog
        open={syncDialogOpen}
        onClose={closeSyncDialog}
        onConfirm={handleSyncConfirm}
        operationType={syncOperationType}
        technology={syncTechnology}
        skillLevels={skillLevels}
        loading={syncLoading}
        error={syncError}
      />
    </Container>
  );
};

export default TechnologyComparison;
